import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import { api, queryKeys } from '~/api'
import i18n from '~/i18n'
import { formatReturnMoney } from '~/returns/components'
import type { ReturnsQuery } from '~/returns/schema'
import { useAppSelector } from '~/store'

const PAGE_SIZE = 50
const compactInputStyles = {
    '& .MuiTextField-root': {
        width: 150,
    },
    '& .MuiInputBase-root': {
        height: 30,
        fontSize: 13,
    },
    '& .MuiOutlinedInput-input': {
        p: '6px 12px',
    },
    '& .MuiInputLabel-sizeSmall': {
        fontSize: 13,
        transform: 'translate(14px, 5px) scale(1)',
    },
    '& .MuiInputLabel-sizeSmall.MuiInputLabel-shrink': {
        transform: 'translate(14px, -7px) scale(0.75)',
    },
}

type FilterState = {
    channel: string
    deadline: string
    reason: string
    shop: string
    status: string
    store: string
    type: string
}

const emptyFilters: FilterState = {
    channel: '',
    deadline: '',
    reason: '',
    shop: '',
    status: '',
    store: '',
    type: '',
}

export default function ReturnsPage() {
    const navigate = useNavigate()
    const userId = useAppSelector((state) => state.initial.user.id)
    const [page, setPage] = useState(0)
    const [returnIdDraft, setReturnIdDraft] = useState('')
    const [returnId, setReturnId] = useState('')
    const [skuDraft, setSkuDraft] = useState('')
    const [sku, setSku] = useState('')
    const [actionRequired, setActionRequired] = useState(false)
    const [dateFrom, setDateFrom] = useState(dayjs().subtract(29, 'day').format('YYYY-MM-DD'))
    const [dateTo, setDateTo] = useState(dayjs().format('YYYY-MM-DD'))
    const [filters, setFilters] = useState<FilterState>(emptyFilters)

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setReturnId(returnIdDraft.trim())
            setSku(skuDraft.trim())
            setPage(0)
        }, 350)
        return () => window.clearTimeout(timeout)
    }, [returnIdDraft, skuDraft])

    const query = useMemo<ReturnsQuery>(
        () => ({
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
            ordering: '-date_updated',
            return_id: returnId || undefined,
            sku: sku || undefined,
            date_created_after: dateFrom || undefined,
            date_created_before: dateTo || undefined,
            sale__shop: filters.shop ? Number(filters.shop) : undefined,
            sale__shop__store: filters.store ? Number(filters.store) : undefined,
            sale__shop__channel: filters.channel ? Number(filters.channel) : undefined,
            status: filters.status || undefined,
            return_type: filters.type || undefined,
            reason: filters.reason || undefined,
            deadline_state: filters.deadline || undefined,
            is_action_required: actionRequired || undefined,
        }),
        [actionRequired, dateFrom, dateTo, filters, page, returnId, sku]
    )

    const listQuery = api.returns.list.useQuery(
        queryKeys.returns.list(userId, query).queryKey,
        { params: { userId }, query },
        { keepPreviousData: true }
    )
    const filterQuery = api.returns.filterValues.useQuery(
        queryKeys.returns.filterValues(userId).queryKey,
        { params: { userId } },
        { staleTime: 5 * 60 * 1000 }
    )
    const response = listQuery.data?.status === 200 ? listQuery.data.body : undefined
    const filterValues = filterQuery.data?.status === 200 ? filterQuery.data.body : undefined

    const updateFilter = (name: keyof FilterState, value: string) => {
        setFilters((current) => ({ ...current, [name]: value }))
        setPage(0)
    }

    return (
        <Box sx={{ width: '100%', p: { xs: 2, md: 3 } }}>
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <Box
                    sx={{
                        ...compactInputStyles,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        alignItems: 'center',
                        p: 1.5,
                    }}
                >
                    <TextField
                        size="small"
                        value={returnIdDraft}
                        placeholder={gettext('Return ID')}
                        onChange={(event) => setReturnIdDraft(event.target.value)}
                    />
                    <TextField
                        size="small"
                        value={skuDraft}
                        placeholder={gettext('SKU')}
                        onChange={(event) => setSkuDraft(event.target.value)}
                    />
                    <TextField
                        select
                        size="small"
                        label={gettext('Store')}
                        value={filters.store}
                        onChange={(event) => updateFilter('store', event.target.value)}
                    >
                        <MenuItem value="">{gettext('All stores')}</MenuItem>
                        {filterValues?.stores.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label={gettext('Shop')}
                        value={filters.shop}
                        onChange={(event) => updateFilter('shop', event.target.value)}
                    >
                        <MenuItem value="">{gettext('All shops')}</MenuItem>
                        {filterValues?.shops.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label={gettext('Channel')}
                        value={filters.channel}
                        onChange={(event) => updateFilter('channel', event.target.value)}
                    >
                        <MenuItem value="">{gettext('All channels')}</MenuItem>
                        {filterValues?.channels.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label={gettext('Status')}
                        value={filters.status}
                        onChange={(event) => updateFilter('status', event.target.value)}
                    >
                        <MenuItem value="">{gettext('All statuses')}</MenuItem>
                        {filterValues?.statuses.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label={gettext('Deadline')}
                        value={filters.deadline}
                        onChange={(event) => updateFilter('deadline', event.target.value)}
                    >
                        <MenuItem value="">{gettext('Any deadline')}</MenuItem>
                        {filterValues?.deadlines.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Box
                    sx={{
                        ...compactInputStyles,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        alignItems: 'center',
                        px: 1.5,
                        pb: 1.5,
                    }}
                >
                    <TextField
                        size="small"
                        type="date"
                        label={gettext('Created from')}
                        value={dateFrom}
                        onChange={(event) => {
                            setDateFrom(event.target.value)
                            setPage(0)
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label={gettext('Created to')}
                        value={dateTo}
                        onChange={(event) => {
                            setDateTo(event.target.value)
                            setPage(0)
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        select
                        size="small"
                        label={gettext('Return type')}
                        value={filters.type}
                        onChange={(event) => updateFilter('type', event.target.value)}
                    >
                        <MenuItem value="">{gettext('All types')}</MenuItem>
                        {filterValues?.types.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label={gettext('Reason')}
                        value={filters.reason}
                        onChange={(event) => updateFilter('reason', event.target.value)}
                    >
                        <MenuItem value="">{gettext('All reasons')}</MenuItem>
                        {filterValues?.reasons.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <FormControlLabel
                        sx={{ ml: 0.5 }}
                        control={
                            <Switch
                                size="small"
                                checked={actionRequired}
                                onChange={(event) => {
                                    setActionRequired(event.target.checked)
                                    setPage(0)
                                }}
                            />
                        }
                        label={gettext('Action required')}
                    />
                </Box>

                <TableContainer sx={{ maxHeight: 'calc(100vh - 285px)', minHeight: 280 }}>
                    <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 1080 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 58 }} />
                                <TableCell sx={{ width: 155 }}>{gettext('Return ID')}</TableCell>
                                <TableCell>{gettext('Item')}</TableCell>
                                <TableCell sx={{ width: 145 }}>{gettext('Shop')}</TableCell>
                                <TableCell sx={{ width: 135 }}>{gettext('Return type')}</TableCell>
                                <TableCell align="right" sx={{ width: 110 }}>
                                    {gettext('Refund')}
                                </TableCell>
                                <TableCell sx={{ width: 155 }}>{gettext('Reason')}</TableCell>
                                <TableCell align="center" sx={{ width: 145 }}>
                                    {gettext('Status')}
                                </TableCell>
                                <TableCell align="center" sx={{ width: 80 }}>
                                    {gettext('Channel')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {listQuery.isLoading && !response ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 10 }}>
                                        <CircularProgress size={30} color="secondary" />
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {listQuery.isError ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 10 }}>
                                        <Typography color="error">
                                            {gettext('Returns could not be loaded.')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {!listQuery.isLoading && response?.results.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={9}
                                        align="center"
                                        sx={{ py: 10, color: 'text.disabled' }}
                                    >
                                        {gettext('No returns match these filters.')}
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {response?.results.map((item) => {
                                const shopName = item.shop.shop_name
                                    .replace(item.shop.channel.name, '')
                                    .replace(/\s+/g, ' ')
                                    .trim()
                                return (
                                    <TableRow
                                        hover
                                        key={item.id}
                                        onClick={() => navigate(`/home/sales/returns/${item.id}`)}
                                        sx={{
                                            cursor: 'pointer',
                                            '& td': { py: 0.75, fontSize: '0.85rem' },
                                        }}
                                    >
                                        <TableCell>
                                            <Box
                                                component="img"
                                                src={
                                                    item.cover_image_url ||
                                                    '/static/web/img/placeholder.png'
                                                }
                                                alt=""
                                                sx={{ width: 40, height: 40, objectFit: 'contain' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap>
                                                {item.return_id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap>
                                                {item.first_item_sku || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap>
                                                {shopName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap>
                                                {item.return_type_display_name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatReturnMoney(item.refund_total, item.currency)}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap>
                                                {item.reason_display_name ||
                                                    gettext('Other reason')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {item.status_display_name ||
                                                    item.status ||
                                                    gettext('Unknown')}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {item.seller_action_due_at
                                                    ? dayjs(item.seller_action_due_at)
                                                          .locale(i18n.language)
                                                          .format('D MMM HH:mm')
                                                    : '—'}
                                            </Typography>
                                            {item.is_action_required ? (
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    sx={{
                                                        display: 'inline-block',
                                                        mt: 0.25,
                                                        border: '1px solid',
                                                        borderColor: item.is_overdue
                                                            ? 'error.light'
                                                            : 'primary.main',
                                                        borderRadius: '4px',
                                                        color: item.is_overdue
                                                            ? 'error.main'
                                                            : 'primary.main',
                                                        fontSize: 10,
                                                        p: '3px',
                                                    }}
                                                >
                                                    {item.is_overdue
                                                        ? gettext('Overdue')
                                                        : gettext('Action required')}
                                                </Typography>
                                            ) : null}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box
                                                component="img"
                                                src={
                                                    item.shop.channel.channel_square_icon_url ||
                                                    item.shop.channel.channel_icon_url ||
                                                    ''
                                                }
                                                title={item.shop.channel.name}
                                                alt={item.shop.channel.name}
                                                sx={{ width: 25, height: 25, objectFit: 'contain' }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={response?.count ?? 0}
                    page={page}
                    rowsPerPage={PAGE_SIZE}
                    rowsPerPageOptions={[PAGE_SIZE]}
                    onPageChange={(_, nextPage) => setPage(nextPage)}
                />
            </Paper>
        </Box>
    )
}
